import dayjs from "dayjs";
import { hasValidCoordinates } from "@shared/helpers/geo";
import {
  OPPORTUNITY_TITLE_MAX_LENGTH,
  TIME_DESCRIPTION_MAX_LENGTH,
  VOLUNTEER_DESCRIPTION_MAX_LENGTH,
  WEEK_DAYS,
} from "@shared/helpers/constants";
import { MATCH_WEIGHT_MAX, MATCH_WEIGHT_MIN } from "@shared/helpers/match-weight";
import {
  OpportunityVolunteerMode,
  OPPORTUNITY_VOLUNTEER_MODE_HEB,
  type OpportunityVolunteerModeType,
} from "@shared/helpers/opportunity-volunteer-mode";
import {
  VolunteerRegistrationOutcome,
  type VolunteerRegistrationOutcomeType,
} from "@shared/helpers/volunteer-registration-outcome";

export const GROUP_CHILD_BULK_REMOTE_LOCATION = "מרחוק";

export type GroupChildBulkUpdatePatchInput = {
  title: string;
  description: string;
  imageId?: string | null;
  associationRequestedId?: string | null;
  location: string;
  coordinates: { lat: number; lng: number };
  isRemote: boolean;
  hasTime: boolean;
  hasStartTime: boolean;
  startDate: Date | string | null;
  endDate: Date | string | null;
  timeDescription: string;
  amount?: number;
  /** Written only when true. A missing flag does not change the schedule. */
  applySchedule?: boolean;
  /** When true, location and coordinates are written to every selected slot. */
  applyLocation?: boolean;
  /** When true, `lastCallToVolunteers` is written to every selected slot. */
  applyLastCall?: boolean;
  lastCallToVolunteers?: Date | string | null;
  applyTitle?: boolean;
  applyDescription?: boolean;
  applyImage?: boolean;
  applyAmount?: boolean;
  applyRequester?: boolean;
  applyComments?: boolean;
  comments?: string | null;
  applyAge?: boolean;
  minAge?: number;
  maxAge?: number;
  applyRequiredGender?: boolean;
  requiredGender?: "MALE" | "FEMALE" | null;
  applyRequiresVehicle?: boolean;
  requiresVehicle?: boolean;
  applyIsSensitive?: boolean;
  isSensitive?: boolean;
  applyVolunteerMode?: boolean;
  volunteerMode?: OpportunityVolunteerModeType;
  applyIsLongTerm?: boolean;
  isLongTerm?: boolean;
  applyIsHidden?: boolean;
  isHidden?: boolean;
  applyTeams?: boolean;
  teamIds?: string[];
  applyCategories?: boolean;
  categoryIds?: string[];
  applyPopulations?: boolean;
  populationIds?: string[];
  applyRegistrationFlow?: boolean;
  volunteerRegistrationFlow?: VolunteerRegistrationOutcomeType;
  applyMatchWeight?: boolean;
  defaultMatchWeight?: number;
  applyDailyTarget?: boolean;
  dailyVolunteerTarget?: number | null;
  applyManagerUrgency?: boolean;
  managerUrgency?: "STANDARD" | "HIGH" | "CRITICAL";
  applyCallCenterUrgency?: boolean;
  callCenterUrgency?: number;
  applyCallCenterDifficulty?: boolean;
  callCenterDifficulty?: number;
  applyWhatsapp?: boolean;
  whatsappGroupUrl?: string | null;
};

export type GroupChildBulkWrite = Partial<GroupChildBulkUpdateFields> & {
  comments?: string;
  minAge?: number;
  maxAge?: number;
  requiredGender?: "MALE" | "FEMALE" | null;
  requiresVehicle?: boolean;
  isSensitive?: boolean;
  volunteerMode?: OpportunityVolunteerModeType;
  isLongTerm?: boolean;
  isHidden?: boolean;
  volunteerRegistrationFlow?: VolunteerRegistrationOutcomeType;
  defaultMatchWeight?: number;
  dailyVolunteerTarget?: number | null;
  managerUrgency?: "STANDARD" | "HIGH" | "CRITICAL";
  callCenterUrgency?: number;
  callCenterDifficulty?: number;
  whatsappGroupUrl?: string;
};

const MANAGER_URGENCIES = ["STANDARD", "HIGH", "CRITICAL"] as const;
const REGISTRATION_FLOWS = new Set<string>(Object.values(VolunteerRegistrationOutcome));
const VOLUNTEER_MODES = new Set<string>(Object.values(OpportunityVolunteerMode));

export function bulkUpdateHasSelection(
  patch: GroupChildBulkUpdatePatchInput,
): boolean {
  return (
    patch.applyTitle === true ||
    patch.applyDescription === true ||
    patch.applyImage === true ||
    patch.applyAmount === true ||
    patch.applyRequester === true ||
    patch.applyComments === true ||
    patch.applyAge === true ||
    patch.applyRequiredGender === true ||
    patch.applyRequiresVehicle === true ||
    patch.applyIsSensitive === true ||
    patch.applyVolunteerMode === true ||
    patch.applyIsLongTerm === true ||
    patch.applyIsHidden === true ||
    patch.applyTeams === true ||
    patch.applyCategories === true ||
    patch.applyPopulations === true ||
    patch.applyRegistrationFlow === true ||
    patch.applyMatchWeight === true ||
    patch.applyDailyTarget === true ||
    patch.applyManagerUrgency === true ||
    patch.applyCallCenterUrgency === true ||
    patch.applyCallCenterDifficulty === true ||
    patch.applyWhatsapp === true ||
    patch.applySchedule === true ||
    patch.applyLocation === true ||
    patch.applyLastCall === true
  );
}

export type GroupChildBulkUpdateFields = {
  title: string;
  description: string;
  imageId: string;
  associationRequestedId: string | null;
  location: string;
  coordinates: { lat: number; lng: number };
  isRemote: boolean;
  amount: number;
  hasTime: boolean;
  hasStartTime: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timeDescription: string;
  day: (typeof WEEK_DAYS)[number];
  lastCallToVolunteers: Date;
};

export type GroupChildBulkExistingSchedule = {
  lastCallToVolunteers?: Date | null;
  day?: (typeof WEEK_DAYS)[number];
};

export function parseBulkUpdateDate(
  value: Date | string | null | undefined,
): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

export function applyPickedDateKeepingTime(
  current: Date | null,
  picked: Date,
): Date {
  const next = new Date(picked);
  if (current && Number.isFinite(current.getTime())) {
    next.setHours(
      current.getHours(),
      current.getMinutes(),
      current.getSeconds(),
      current.getMilliseconds(),
    );
  }
  return next;
}

export function applyPickedCalendarDate(args: {
  hasStartTime: boolean;
  startDate: Date | null;
  endDate: Date | null;
  picked: Date;
}): { startDate: Date | null; endDate: Date } {
  const { hasStartTime, startDate, endDate, picked } = args;
  if (!hasStartTime) {
    return {
      startDate,
      endDate: applyPickedDateKeepingTime(endDate, picked),
    };
  }
  if (startDate && endDate) {
    const duration = Math.max(0, endDate.getTime() - startDate.getTime());
    const nextStart = applyPickedDateKeepingTime(startDate, picked);
    return {
      startDate: nextStart,
      endDate: new Date(nextStart.getTime() + duration),
    };
  }
  return {
    startDate: applyPickedDateKeepingTime(startDate, picked),
    endDate: applyPickedDateKeepingTime(endDate, picked),
  };
}

function parseAmount(value: number | undefined): number | null {
  if (value == null) return null;
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 1 || !Number.isInteger(amount)) {
    return null;
  }
  return amount;
}

export function validateGroupChildBulkUpdatePatch(
  patch: GroupChildBulkUpdatePatchInput,
): string | null {
  if (!bulkUpdateHasSelection(patch)) {
    return "יש לסמן לפחות שדה אחד לעדכון";
  }

  if (patch.applyTitle === true) {
    const title = patch.title?.trim() ?? "";
    if (title.length < 2) {
      return "הכותרת צריכה להכיל לפחות 2 תווים";
    }
    if (title.length > OPPORTUNITY_TITLE_MAX_LENGTH) {
      return `הכותרת יכולה להכיל עד ${OPPORTUNITY_TITLE_MAX_LENGTH} תווים`;
    }
  }

  if (patch.applyDescription === true) {
    const description = patch.description?.trim() ?? "";
    if (description.length < 5) {
      return "התיאור צריך להכיל לפחות 5 תווים";
    }
    if (description.length > VOLUNTEER_DESCRIPTION_MAX_LENGTH) {
      return `התיאור יכול להכיל עד ${VOLUNTEER_DESCRIPTION_MAX_LENGTH} תווים`;
    }
  }

  if (patch.applyImage === true && !patch.imageId?.trim()) {
    return "יש לבחור תמונה";
  }

  if (patch.applyAmount === true && parseAmount(patch.amount) == null) {
    return "יש להזין לפחות מקום אחד";
  }

  if (patch.applyAge === true) {
    const minAge = Number(patch.minAge);
    const maxAge = Number(patch.maxAge);
    if (!Number.isInteger(minAge) || minAge < 6) {
      return "גיל מינימלי צריך להיות לפחות 6";
    }
    if (!Number.isInteger(maxAge) || maxAge > 120) {
      return "גיל מקסימלי צריך להיות עד 120";
    }
    if (maxAge < minAge) {
      return "גיל מקסימלי חייב להיות גדול או שווה לגיל המינימלי";
    }
  }

  if (
    patch.applyRequiredGender === true &&
    patch.requiredGender != null &&
    patch.requiredGender !== "MALE" &&
    patch.requiredGender !== "FEMALE"
  ) {
    return "מגדר לא תקין";
  }

  if (
    patch.applyVolunteerMode === true &&
    !VOLUNTEER_MODES.has(patch.volunteerMode ?? "")
  ) {
    return "סוג התנדבות לא תקין";
  }

  if (
    patch.applyRegistrationFlow === true &&
    !REGISTRATION_FLOWS.has(patch.volunteerRegistrationFlow ?? "")
  ) {
    return "זרימת הרשמה לא תקינה";
  }

  if (patch.applyMatchWeight === true) {
    const weight = Number(patch.defaultMatchWeight);
    if (
      !Number.isInteger(weight) ||
      weight < MATCH_WEIGHT_MIN ||
      weight > MATCH_WEIGHT_MAX
    ) {
      return `משקל מאץ' בין ${MATCH_WEIGHT_MIN} ל-${MATCH_WEIGHT_MAX}`;
    }
  }

  if (patch.applyDailyTarget === true && patch.dailyVolunteerTarget != null) {
    const target = Number(patch.dailyVolunteerTarget);
    if (!Number.isInteger(target) || target < 1) {
      return "יעד יומי חייב להיות לפחות 1, או ריק";
    }
  }

  if (
    patch.applyManagerUrgency === true &&
    !MANAGER_URGENCIES.includes(
      patch.managerUrgency as (typeof MANAGER_URGENCIES)[number],
    )
  ) {
    return "דחיפות מנהל לא תקינה";
  }

  if (patch.applyCallCenterUrgency === true) {
    const urgency = Number(patch.callCenterUrgency);
    if (!Number.isInteger(urgency) || urgency < 0 || urgency > 10) {
      return "רמת דחיפות בין 0 ל-10";
    }
  }

  if (patch.applyCallCenterDifficulty === true) {
    const difficulty = Number(patch.callCenterDifficulty);
    if (!Number.isInteger(difficulty) || difficulty < 1 || difficulty > 5) {
      return "רמת קושי בין 1 ל-5";
    }
  }

  if (patch.applySchedule === true) {
    if (!patch.hasTime) {
      const timeDescription = patch.timeDescription?.trim() ?? "";
      if (!timeDescription) {
        return "יש למלא תיאור זמן";
      }
      if (timeDescription.length > TIME_DESCRIPTION_MAX_LENGTH) {
        return `תיאור הזמן יכול להכיל עד ${TIME_DESCRIPTION_MAX_LENGTH} תווים`;
      }
    } else if (!patch.hasStartTime) {
      const endDate = parseBulkUpdateDate(patch.endDate);
      if (!endDate) {
        return "יש למלא תאריך יעד";
      }
    } else {
      const startDate = parseBulkUpdateDate(patch.startDate);
      const endDate = parseBulkUpdateDate(patch.endDate);
      if (!startDate || !endDate) {
        return "יש למלא תאריך התחלה וסיום";
      }
      if (endDate.getTime() < startDate.getTime()) {
        return "תאריך הסיום חייב להיות אחרי תאריך ההתחלה";
      }
    }
  }

  if (patch.applyLocation) {
    if (!patch.isRemote) {
      if (!patch.location?.trim()) {
        return "יש למלא מיקום או לסמן התנדבות מרחוק";
      }
      if (!hasValidCoordinates(patch.coordinates)) {
        return "יש לבחור מיקום מהרשימה או מהמפה";
      }
    }
  }

  if (patch.applyLastCall && !parseBulkUpdateDate(patch.lastCallToVolunteers)) {
    return "יש למלא מועד אחרון להרשמה";
  }

  if (patch.applyTeams === true && hasBlankBulkId(patch.teamIds)) {
    return "מזהה פרויקט לא תקין";
  }
  if (patch.applyCategories === true && hasBlankBulkId(patch.categoryIds)) {
    return "מזהה קטגוריה לא תקין";
  }
  if (patch.applyPopulations === true && hasBlankBulkId(patch.populationIds)) {
    return "מזהה אוכלוסייה לא תקין";
  }

  return null;
}

export type GroupChildBulkRelations = {
  teamIds?: string[];
  categoryIds?: string[];
  populationIds?: string[];
};

/** Drops blanks and duplicates. An empty result is a clear, not a skip. */
export function normalizeBulkIdList(ids: string[] | undefined): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const raw of ids ?? []) {
    const id = raw?.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }
  return unique;
}

function hasBlankBulkId(ids: string[] | undefined): boolean {
  return (ids ?? []).some((id) => !id?.trim());
}

function sameBulkIdSet(left: string[], right: string[]): boolean {
  const a = normalizeBulkIdList(left);
  const b = normalizeBulkIdList(right);
  if (a.length !== b.length) return false;
  const rightSet = new Set(b);
  return a.every((id) => rightSet.has(id));
}

/** Rows whose current links differ from the value about to be written. */
export function bulkRelationSensitiveWarning(input: {
  fieldLabel: string;
  nextIds: string[];
  currentSets: string[][];
}): string | null {
  const next = normalizeBulkIdList(input.nextIds);
  const affected = input.currentSets.filter(
    (current) => !sameBulkIdSet(current, next),
  ).length;
  if (affected === 0) return null;
  if (next.length === 0) {
    return `שמירה תמחק את ${input.fieldLabel} מ-${affected} רשומות שכבר משויכות.`;
  }
  return `${input.fieldLabel} יוחלפו בכל הנבחרים. ב-${affected} רשומות השיוך היום שונה, והשיוך הקיים יימחק.`;
}

export function selectGroupChildBulkRelations(
  patch: GroupChildBulkUpdatePatchInput,
): GroupChildBulkRelations {
  const relations: GroupChildBulkRelations = {};
  if (patch.applyTeams === true) {
    relations.teamIds = normalizeBulkIdList(patch.teamIds);
  }
  if (patch.applyCategories === true) {
    relations.categoryIds = normalizeBulkIdList(patch.categoryIds);
  }
  if (patch.applyPopulations === true) {
    relations.populationIds = normalizeBulkIdList(patch.populationIds);
  }
  return relations;
}

export function buildGroupChildBulkUpdateFields(
  patch: GroupChildBulkUpdatePatchInput,
  parentLastCallToVolunteers: Date,
  existing?: GroupChildBulkExistingSchedule,
): GroupChildBulkUpdateFields {
  const hasTime = !!patch.hasTime;
  const hasStartTime = hasTime ? !!patch.hasStartTime : true;
  const startDate = hasTime && hasStartTime ? parseBulkUpdateDate(patch.startDate) : null;
  const endDate = hasTime ? parseBulkUpdateDate(patch.endDate) : null;
  const timeDescription = hasTime
    ? ""
    : (patch.timeDescription?.trim() ?? "").slice(0, TIME_DESCRIPTION_MAX_LENGTH);
  const isRemote = !!patch.isRemote;
  const location = isRemote
    ? GROUP_CHILD_BULK_REMOTE_LOCATION
    : patch.location.trim();
  const coordinates = isRemote
    ? { lat: 0, lng: 0 }
    : patch.coordinates;
  const amount = parseAmount(patch.amount) ?? 1;

  const scheduleAnchor = hasTime ? (hasStartTime ? startDate : endDate) : null;
  const existingLastCall = parseBulkUpdateDate(existing?.lastCallToVolunteers);
  const explicitLastCall = parseBulkUpdateDate(patch.lastCallToVolunteers);
  const lastCallToVolunteers = patch.applyLastCall
    ? (explicitLastCall ?? existingLastCall ?? parentLastCallToVolunteers)
    : (existingLastCall ?? parentLastCallToVolunteers);

  const day = scheduleAnchor
    ? WEEK_DAYS[dayjs(scheduleAnchor).day()]
    : (existing?.day ?? WEEK_DAYS[dayjs().day()]);

  return {
    title: patch.title.trim(),
    description: patch.description.trim(),
    imageId: patch.imageId ?? "",
    associationRequestedId: patch.associationRequestedId || null,
    location,
    coordinates,
    isRemote,
    amount,
    hasTime,
    hasStartTime,
    startDate,
    endDate,
    timeDescription,
    day,
    lastCallToVolunteers,
  };
}

export function pickGroupChildBulkContentFields(
  fields: GroupChildBulkUpdateFields,
) {
  return {
    title: fields.title,
    description: fields.description,
    imageId: fields.imageId,
    associationRequestedId: fields.associationRequestedId,
    location: fields.location,
    coordinates: fields.coordinates,
    isRemote: fields.isRemote,
    amount: fields.amount,
  };
}

/** Fields actually written. A field is included only when its apply flag is true. */
export function selectGroupChildBulkUpdateWrite(
  fields: GroupChildBulkUpdateFields,
  patch: GroupChildBulkUpdatePatchInput,
): GroupChildBulkWrite {
  const write: GroupChildBulkWrite = {};
  if (patch.applyTitle === true) write.title = fields.title;
  if (patch.applyDescription === true) write.description = fields.description;
  if (patch.applyImage === true) write.imageId = fields.imageId;
  if (patch.applyRequester === true) {
    write.associationRequestedId = fields.associationRequestedId;
  }
  if (patch.applyAmount === true) write.amount = fields.amount;
  if (patch.applyComments === true) {
    write.comments = patch.comments?.trim() ?? "";
  }
  if (patch.applyAge === true) {
    write.minAge = Number(patch.minAge);
    write.maxAge = Number(patch.maxAge);
  }
  if (patch.applyRequiredGender === true) {
    write.requiredGender =
      patch.requiredGender === "MALE" || patch.requiredGender === "FEMALE"
        ? patch.requiredGender
        : null;
  }
  if (patch.applyRequiresVehicle === true) {
    write.requiresVehicle = !!patch.requiresVehicle;
  }
  if (patch.applyIsSensitive === true) write.isSensitive = !!patch.isSensitive;
  if (patch.applyVolunteerMode === true && patch.volunteerMode) {
    write.volunteerMode = patch.volunteerMode;
  }
  if (patch.applyIsLongTerm === true) write.isLongTerm = !!patch.isLongTerm;
  if (patch.applyIsHidden === true) write.isHidden = !!patch.isHidden;
  if (patch.applyRegistrationFlow === true && patch.volunteerRegistrationFlow) {
    write.volunteerRegistrationFlow = patch.volunteerRegistrationFlow;
  }
  if (patch.applyMatchWeight === true) {
    write.defaultMatchWeight = Number(patch.defaultMatchWeight);
  }
  if (patch.applyDailyTarget === true) {
    write.dailyVolunteerTarget =
      patch.dailyVolunteerTarget == null
        ? null
        : Number(patch.dailyVolunteerTarget);
  }
  if (patch.applyManagerUrgency === true && patch.managerUrgency) {
    write.managerUrgency = patch.managerUrgency;
  }
  if (patch.applyCallCenterUrgency === true && patch.callCenterUrgency != null) {
    write.callCenterUrgency = Number(patch.callCenterUrgency);
  }
  if (
    patch.applyCallCenterDifficulty === true &&
    patch.callCenterDifficulty != null
  ) {
    write.callCenterDifficulty = Number(patch.callCenterDifficulty);
  }
  if (patch.applyWhatsapp === true) {
    write.whatsappGroupUrl = patch.whatsappGroupUrl?.trim() ?? "";
  }
  if (patch.applyLocation === true) {
    write.location = fields.location;
    write.coordinates = fields.coordinates;
    write.isRemote = fields.isRemote;
  }
  if (patch.applySchedule === true) {
    write.hasTime = fields.hasTime;
    write.hasStartTime = fields.hasStartTime;
    write.startDate = fields.startDate;
    write.endDate = fields.endDate;
    write.timeDescription = fields.timeDescription;
    write.day = fields.day;
  }
  if (patch.applyLastCall === true) {
    write.lastCallToVolunteers = fields.lastCallToVolunteers;
  }
  return write;
}

/** Parent keeps shared identity fields plus long-term and hidden when those were checked. */
export function pickParentBulkUpdate(
  write: GroupChildBulkWrite,
): Pick<
  GroupChildBulkWrite,
  | "title"
  | "description"
  | "imageId"
  | "associationRequestedId"
  | "isLongTerm"
  | "isHidden"
> {
  const parent: Pick<
    GroupChildBulkWrite,
    | "title"
    | "description"
    | "imageId"
    | "associationRequestedId"
    | "isLongTerm"
    | "isHidden"
  > = {};
  if (write.title != null) parent.title = write.title;
  if (write.description != null) parent.description = write.description;
  if (write.imageId != null) parent.imageId = write.imageId;
  if ("associationRequestedId" in write) {
    parent.associationRequestedId = write.associationRequestedId;
  }
  if (write.isLongTerm !== undefined) parent.isLongTerm = write.isLongTerm;
  if (write.isHidden !== undefined) parent.isHidden = write.isHidden;
  return parent;
}

export function formatBulkScheduleSummary(input: {
  hasTime: boolean;
  hasStartTime: boolean;
  startDate: Date | string | null | undefined;
  endDate: Date | string | null | undefined;
  timeDescription?: string | null;
}): string {
  if (!input.hasTime) {
    const text = input.timeDescription?.trim();
    return text ? `בלי זמן מוגדר: ${text}` : "בלי זמן מוגדר";
  }
  const start = parseBulkUpdateDate(input.startDate);
  const end = parseBulkUpdateDate(input.endDate);
  if (!input.hasStartTime) {
    return end ? `עד ${dayjs(end).format("DD/MM/YYYY HH:mm")}` : "עד תאריך";
  }
  if (!start || !end) return "מועד חסר";
  const sameDay = dayjs(start).isSame(end, "day");
  return sameDay
    ? `${dayjs(start).format("DD/MM/YYYY HH:mm")} - ${dayjs(end).format("HH:mm")}`
    : `${dayjs(start).format("DD/MM/YYYY HH:mm")} - ${dayjs(end).format("DD/MM/YYYY HH:mm")}`;
}

export function formatBulkLocationSummary(input: {
  isRemote?: boolean;
  location?: string | null;
}): string {
  if (input.isRemote) return GROUP_CHILD_BULK_REMOTE_LOCATION;
  return input.location?.trim() || "בלי מיקום";
}

export function formatBulkLastCallSummary(
  value: Date | string | null | undefined,
): string {
  const date = parseBulkUpdateDate(value);
  return date ? dayjs(date).format("DD/MM/YYYY HH:mm") : "לא הוגדר";
}

const URGENCY_HEB: Record<"STANDARD" | "HIGH" | "CRITICAL", string> = {
  STANDARD: "רגילה",
  HIGH: "גבוהה",
  CRITICAL: "קריטית",
};

const FLOW_HEB: Record<VolunteerRegistrationOutcomeType, string> = {
  REGULAR: "הרשמה רגילה",
  INTERESTED: "התעניינות (ממתין לאישור)",
  CHAT: "הרשמה וצ'אט עם מבקש הסיוע",
  SMS_DETAILS: "הרשמה ופרטי מבקש ב-SMS",
};

export function formatBulkContentValue(
  field: keyof GroupChildBulkWrite,
  write: GroupChildBulkWrite,
): string {
  if (field === "requiredGender") {
    if (write.requiredGender === "MALE") return "זכר";
    if (write.requiredGender === "FEMALE") return "נקבה";
    return "כולם";
  }
  if (field === "volunteerMode" && write.volunteerMode) {
    return OPPORTUNITY_VOLUNTEER_MODE_HEB[write.volunteerMode];
  }
  if (field === "volunteerRegistrationFlow" && write.volunteerRegistrationFlow) {
    return FLOW_HEB[write.volunteerRegistrationFlow];
  }
  if (field === "managerUrgency" && write.managerUrgency) {
    return URGENCY_HEB[write.managerUrgency];
  }
  if (field === "callCenterUrgency" && write.callCenterUrgency != null) {
    return write.callCenterUrgency === 0
      ? "0 - ללא"
      : String(write.callCenterUrgency);
  }
  if (field === "callCenterDifficulty" && write.callCenterDifficulty != null) {
    const difficulty = write.callCenterDifficulty;
    if (difficulty <= 2) return `${difficulty} - קלה`;
    if (difficulty === 3) return "3 - בינונית";
    return `${difficulty} - קשה`;
  }
  if (field === "dailyVolunteerTarget") {
    return write.dailyVolunteerTarget == null
      ? "בלי יעד"
      : String(write.dailyVolunteerTarget);
  }
  if (field === "minAge") return `${write.minAge ?? ""}-${write.maxAge ?? ""}`;
  const value = write[field];
  if (typeof value === "boolean") return value ? "כן" : "לא";
  if (value == null || value === "") return "ריק";
  return String(value);
}
